import { z } from "zod";

/**
 * Thin client over the Flask API.
 *
 * Auth stays exactly as it was: a Flask session cookie, sent same-origin. No
 * tokens are stored in JS, which keeps the existing session-revocation and
 * passkey flows working untouched.
 */

export class ApiError extends Error {
  // Declared as fields rather than constructor parameter properties, which
  // `erasableSyntaxOnly` disallows.
  readonly status: number;
  readonly code?: string;
  readonly payload?: unknown;

  constructor(status: number, message: string, code?: string, payload?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.payload = payload;
  }

  /** Session expired or never existed — the router redirects to login. */
  get isUnauthenticated() {
    return this.status === 401 || this.status === 403;
  }

  get isQuotaExceeded() {
    return this.status === 429;
  }
}

/**
 * Flask validates a CSRF token on mutating requests. It used to arrive as a
 * Jinja <meta> tag, which no longer exists, so we fetch it once and cache it.
 */
let csrfToken: string | null = null;
let csrfInFlight: Promise<string> | null = null;

async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  if (csrfInFlight) return csrfInFlight;

  csrfInFlight = fetch("/api/csrf", { credentials: "same-origin" })
    .then(async (res) => {
      if (!res.ok) throw new ApiError(res.status, "Gagal mengambil token CSRF.");
      const body = (await res.json()) as { csrf_token?: string };
      if (!body.csrf_token) throw new ApiError(500, "Token CSRF tidak valid.");
      csrfToken = body.csrf_token;
      return csrfToken;
    })
    .finally(() => {
      csrfInFlight = null;
    });

  return csrfInFlight;
}

/** Called after logout so the next session does not reuse a stale token. */
export function clearCsrfToken() {
  csrfToken = null;
}

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Plain object is sent as JSON; FormData is sent as-is (MRI/avatar upload). */
  body?: unknown;
  signal?: AbortSignal;
};

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  const isFormData = options.body instanceof FormData;

  if (method !== "GET") {
    headers["X-CSRF-Token"] = await getCsrfToken();
    if (!isFormData) headers["Content-Type"] = "application/json";
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: "same-origin",
      signal: options.signal,
      body: isFormData
        ? (options.body as FormData)
        : options.body !== undefined
          ? JSON.stringify(options.body)
          : undefined,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "Tidak dapat terhubung ke server. Periksa koneksi Anda.");
  }

  // Nginx rejects oversized uploads with an HTML error page, not JSON, so the
  // content type has to be checked before parsing.
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload: unknown = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const detail = payload as { reply?: string; error?: string; message?: string } | null;
    throw new ApiError(
      response.status,
      detail?.reply ??
        detail?.message ??
        (response.status === 413
          ? "Berkas terlalu besar untuk diunggah."
          : "Terjadi kesalahan pada server."),
      detail?.error,
      payload,
    );
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    // A shape mismatch is a bug, not a user error. Surface it loudly in dev.
    if (import.meta.env.DEV) {
      console.error(`Response shape mismatch for ${path}`, parsed.error.issues, payload);
    }
    throw new ApiError(response.status, "Format data dari server tidak dikenali.");
  }

  return parsed.data;
}

export const api = {
  get: <T>(path: string, schema: z.ZodType<T>, signal?: AbortSignal) =>
    request(path, schema, { signal }),

  post: <T>(path: string, schema: z.ZodType<T>, body?: unknown) =>
    request(path, schema, { method: "POST", body }),

  put: <T>(path: string, schema: z.ZodType<T>, body?: unknown) =>
    request(path, schema, { method: "PUT", body }),

  delete: <T>(path: string, schema: z.ZodType<T>, body?: unknown) =>
    request(path, schema, { method: "DELETE", body }),
};

/** Endpoints that only confirm success carry no useful body. */
export const okSchema = z.looseObject({ ok: z.boolean().optional() });
